import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * @param {object} param
 * @param {User[]} param.users
 * @param {FlightBrief} param.flightBrief
 * @returns {{
 * FLIGHT_BRIEF_SCHEMA: import('@zolteam/zol-react-form/types').BasicSchema,
 * flightBriefDefaultValues: any,
 * splitBriefForm: Function
 * }}
 */
export const useFlightBrief = ({ users, flightBrief }) => {
  const { t } = useTranslation();

  const adminOptions = useMemo(() => [''].concat(users?.map(
    /**
     * @param {User} admin
     * @returns {string}
     */
    ({ firstname, lastname }) => `${firstname} ${lastname}`,
  )), [users]);

  const FLIGHT_BRIEF_SCHEMA = useMemo(() => ({
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    type: 'object',
    title: 'FlightBrief',
    properties: {
      flightNumber: {
        type: 'string',
        description: t('flightBriefForm.fields.flightNumber'),
        x_layout: '1/1/1/5',
      },
      flightDuration: {
        type: 'string',
        description: t('flightBriefForm.fields.flightDuration'),
        x_layout: '1/5/1/8',
      },
      flightRadarLink: {
        type: 'string',
        description: t('flightBriefForm.fields.flightRadarLink'),
        x_layout: '1/1/1/7',
      },
      planeType: {
        type: 'string',
        description: t('flightBriefForm.fields.planeType'),
        x_layout: '2/1/2/4',
      },
      planeRegistration: {
        type: 'string',
        description: t('flightBriefForm.fields.planeRegistration'),
        x_layout: '2/4/2/7',
      },
      planeSeats: {
        type: 'number',
        description: t('flightBriefForm.fields.planeSeats'),
        x_layout: '2/7/2/9',
      },
      departureTerminalId: {
        type: 'string',
        description: t('flightBriefForm.fields.departureTerminalId'),
        x_layout: '3/6/3/9',
      },
      departureTerminalAddress: {
        type: 'string',
        description: t('flightBriefForm.fields.departureTerminalAddress'),
        x_layout: '4/1/4/7',
      },
      departureTerminalCoordinate: {
        type: 'string',
        description: t('flightBriefForm.fields.departureTerminalCoordinate'),
        x_layout: '4/7/4/13',
      },
      departureTerminalPhone: {
        type: 'string',
        description: t('flightBriefForm.fields.departureTerminalPhone'),
        x_layout: '5/1/5/4',
      },
      departureTerminalOpeningTime: {
        type: 'string',
        format: 'time',
        description: t('flightBriefForm.fields.departureTerminalOpeningTime'),
        x_layout: '5/4/5/6',
      },
      departureTerminalClosingTime: {
        type: 'string',
        format: 'time',
        description: t('flightBriefForm.fields.departureTerminalClosingTime'),
        x_layout: '5/6/5/8',
      },
      departureTerminalMeetingInfo: {
        type: 'string',
        fomat: 'long-text',
        description: t('flightBriefForm.fields.departureTerminalMeetingInfo'),
        x_layout: '6/1/6/13',
      },
      arrivalTerminalName: {
        type: 'string',
        description: t('flightBriefForm.fields.arrivalTerminalName'),
        x_layout: '3/1/3/6',
      },
      arrivalTerminalId: {
        type: 'string',
        description: t('flightBriefForm.fields.arrivalTerminalId'),
        x_layout: '3/6/3/9',
      },
      arrivalTerminalAddress: {
        type: 'string',
        description: t('flightBriefForm.fields.arrivalTerminalAddress'),
        x_layout: '4/1/4/7',
      },
      arrivalTerminalCoordinate: {
        type: 'string',
        description: t('flightBriefForm.fields.arrivalTerminalCoordinate'),
        x_layout: '4/7/4/13',
      },
      arrivalTerminalPhone: {
        type: 'string',
        description: t('flightBriefForm.fields.arrivalTerminalPhone'),
        x_layout: '5/1/5/4',
      },
      arrivalTerminalOpeningTime: {
        type: 'string',
        format: 'time',
        description: t('flightBriefForm.fields.arrivalTerminalOpeningTime'),
        x_layout: '5/4/5/6',
      },
      arrivalTerminalClosingTime: {
        type: 'string',
        format: 'time',
        description: t('flightBriefForm.fields.arrivalTerminalClosingTime'),
        x_layout: '5/6/5/8',
      },
      arrivalTerminalMeetingInfo: {
        type: 'string',
        format: 'long-text',
        description: t('flightBriefForm.fields.arrivalTerminalMeetingInfo'),
        x_layout: '6/1/6/13',
      },
      information: {
        type: 'string',
        format: 'long-text',
        description: t('flightBriefForm.fields.information'),
        x_layout: '2/1/2/13',
      },
      displayCompanyLogo: {
        type: 'boolean',
        description: t('flightBriefForm.fields.displayCompanyLogo'),
        x_layout: '3/1/3/13',
      },
    },
    required: ['flightNumber'],
    additionalProperties: false,
  }), [adminOptions]);

  const flightBriefDefaultValues = useMemo(() => ({
    flightDuration: flightBrief?.flightDuration || '',
    flightNumber: flightBrief?.flightNumber || '',
    flightRadarLink: flightBrief?.flightRadarLink || '',
    planeType: flightBrief?.aircraft.type || '',
    planeRegistration: flightBrief?.aircraft.registration || '',
    planeSeats: flightBrief?.aircraft.seatNumber?.toString() || '',
    departureTerminalId: flightBrief?.departureTerminal?.terminalId || '',
    departureTerminalAddress: flightBrief?.departureTerminal?.address || '',
    departureTerminalCoordinate: flightBrief?.departureTerminal?.coordinate?.join(',') || '',
    departureTerminalPhone: flightBrief?.departureTerminal?.phone || '',
    departureTerminalOpeningTime: flightBrief?.departureTerminal?.openingTime || '',
    departureTerminalClosingTime: flightBrief?.departureTerminal?.closingTime || '',
    departureTerminalMeetingInfo: flightBrief?.departureMeetingInfo || '',
    arrivalTerminalName: flightBrief?.arrivalTerminal?.name || '',
    arrivalTerminalId: flightBrief?.arrivalTerminal?.terminalId || '',
    arrivalTerminalAddress: flightBrief?.arrivalTerminal?.address || '',
    arrivalTerminalCoordinate: flightBrief?.arrivalTerminal?.coordinate?.join(',') || '',
    arrivalTerminalPhone: flightBrief?.arrivalTerminal?.phone || '',
    arrivalTerminalOpeningTime: flightBrief?.arrivalTerminal?.openingTime || '',
    arrivalTerminalClosingTime: flightBrief?.arrivalTerminal?.closingTime || '',
    arrivalTerminalMeetingInfo: flightBrief?.arrivalMeetingInfo || '',
    information: flightBrief?.information || '',
    dutyOfficerId: flightBrief?.dutyOfficer?.id || '',
    passengerIdList: flightBrief?.passengersList?.map((passenger) => passenger.id) || [],
    displayCompanyLogo: flightBrief?.displayCompanyLogo ?? true,
  }), [flightBrief]);

  const flightFields = ['flightDuration', 'flightNumber'];
  const planeFields = ['planeType', 'planeRegistration', 'planeSeats'];
  const departureLocationFields = [
    'departureTerminalId',
    'departureTerminalAddress',
    'departureTerminalCoordinate',
    'departureTerminalPhone',
    'departureTerminalOpeningTime',
    'departureTerminalClosingTime',
    'departureTerminalMeetingInfo',
  ];
  const arrivalLocationFields = [
    'arrivalTerminalName',
    'arrivalTerminalId',
    'arrivalTerminalAddress',
    'arrivalTerminalCoordinate',
    'arrivalTerminalPhone',
    'arrivalTerminalOpeningTime',
    'arrivalTerminalClosingTime',
    'arrivalTerminalMeetingInfo',
  ];
  const infoFields = ['information', 'flightRadarLink', 'displayCompanyLogo'];

  /**
   * @param {import('@zolteam/zol-react-form/types').ConfigObject} formConfig
   * @returns {object} The split booking
   */
  const splitBriefForm = (formConfig) => Object.entries(formConfig)
    .reduce((acc, [key, value]) => {
      if (flightFields.includes(key)) {
        return { ...acc, flightPart: { ...acc.flightPart, [key]: value } };
      }
      if (planeFields.includes(key)) {
        return { ...acc, planePart: { ...acc.planePart, [key]: value } };
      }
      if (departureLocationFields.includes(key)) {
        return { ...acc, departureLocationPart: { ...acc.departureLocationPart, [key]: value } };
      }
      if (arrivalLocationFields.includes(key)) {
        return { ...acc, arrivalLocationPart: { ...acc.arrivalLocationPart, [key]: value } };
      }
      if (infoFields.includes(key)) {
        return { ...acc, infoPart: { ...acc.infoPart, [key]: value } };
      }
      return acc;
    }, {
      flightPart: {},
      planePart: {},
      departureLocationPart: {},
      arrivalLocationPart: {},
      infoPart: {},
    });

  return {
    FLIGHT_BRIEF_SCHEMA,
    flightBriefDefaultValues,
    splitBriefForm,
  };
};
